'use client';

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, ArrowRight, Loader2, FileText, Receipt, FileCheck, Download } from 'lucide-react';

import { useAuth } from '@/context/AuthContext';


interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installSuccess, setInstallSuccess] = useState(false);
  const { login } = useAuth();

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setInstallSuccess(true);
      setInstallPrompt(null);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', remember: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setAuthError('');
    const result = await login(data.email, data.password);
    if (result.success) {
      window.location.href = '/';
    } else {
      setAuthError(result.error || 'Invalid credentials. Please check your email and password.');
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[50%] bg-primary flex-col justify-between p-10 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-80px] right-[-80px] w-80 h-80 rounded-full bg-white/5" />
          <div className="absolute bottom-[-60px] left-[-60px] w-64 h-64 rounded-full bg-white/5" />
          <div className="absolute top-1/3 left-1/4 w-40 h-40 rounded-full bg-white/[0.03]" />
        </div>

        <div className="relative">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center overflow-hidden">
              <img
                src="/assets/images/1212-300x300-1786993879424.png"
                alt="Kuttingedge Solutions Logo"
                className="w-10 h-10 object-contain"
              />
            </div>
            <div>
              <p className="text-white font-700 text-lg leading-tight">Kuttingedge</p>
              <p className="text-white/60 text-xs">Solutions · Media &amp; Events</p>
            </div>
          </div>

          <h2 className="text-3xl font-800 text-white leading-snug mb-4">
            Document management<br />made simple.
          </h2>
          <p className="text-white/70 text-sm leading-relaxed max-w-xs">
            Create invoices, receipts, and quotations with automatic VAT at 15%. Print, download, and track every document in one place.
          </p>
        </div>

        {/* Feature list */}
        <div className="relative space-y-3">
          {[
            { icon: FileText, label: 'Invoices with auto-VAT calculation' },
            { icon: Receipt, label: 'Receipts and payment confirmation' },
            { icon: FileCheck, label: 'Quotations with acceptance tracking' },
          ].map((f, i) => {
            const FeatureIcon = f.icon;
            return (
              <div key={`feature-${i}`} className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center shrink-0">
                  <FeatureIcon size={15} className="text-white/80" />
                </div>
                <p className="text-white/80 text-sm">{f.label}</p>
              </div>
            );
          })}
        </div>

        <p className="relative text-white/40 text-xs">
          © 2026 Kuttingedge Solutions. All rights reserved.
        </p>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-10 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <img
              src="/assets/images/1212-300x300-1786993879424.png"
              alt="Kuttingedge Solutions Logo"
              className="w-8 h-8 object-contain"
            />
            <span className="font-700 text-foreground text-lg">Kuttingedge</span>
          </div>

          <div className="mb-6">
            <h1 className="text-2xl font-700 text-foreground">Welcome back</h1>
            <p className="text-sm text-muted-foreground mt-1">Sign in to your Kuttingedge workspace</p>
          </div>

          {/* Auth error */}
          {authError && (
            <div className="mb-4 px-3 py-2.5 bg-danger/10 border border-danger/20 rounded-lg text-xs font-500 text-danger">
              {authError}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-xs font-600 text-foreground mb-1.5">
                Email address <span className="text-danger">*</span>
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@kes.co.zw"
                className={`
                  w-full px-3 py-2.5 text-sm bg-muted border rounded-lg
                  focus:outline-none focus:border-ring focus:bg-card transition-colors duration-150
                  placeholder:text-muted-foreground
                  ${errors.email ? 'border-danger' : 'border-transparent'}
                `}
                {...register('email', {
                  required: 'Email address is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                })}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-danger">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="text-xs font-600 text-foreground">
                  Password <span className="text-danger">*</span>
                </label>
                <button type="button" className="text-xs text-primary hover:underline">
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  className={`
                    w-full px-3 py-2.5 pr-10 text-sm bg-muted border rounded-lg
                    focus:outline-none focus:border-ring focus:bg-card transition-colors duration-150
                    placeholder:text-muted-foreground
                    ${errors.password ? 'border-danger' : 'border-transparent'}
                  `}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 6, message: 'Password must be at least 6 characters' },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-danger">{errors.password.message}</p>
              )}
            </div>

            {/* Remember me */}
            <div className="flex items-center gap-2">
              <input
                id="remember"
                type="checkbox"
                className="w-3.5 h-3.5 rounded border-border accent-primary"
                {...register('remember')}
              />
              <label htmlFor="remember" className="text-xs text-muted-foreground cursor-pointer select-none">
                Remember me for 30 days
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="
                w-full flex items-center justify-center gap-2
                bg-primary text-primary-foreground text-sm font-600
                py-2.5 rounded-lg
                hover:bg-primary/90 active:scale-[0.99] transition-all duration-150
                disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100
              "
            >
              {isLoading ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  Signing in…
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRight size={15} />
                </>
              )}
            </button>
          </form>

          {/* Install App button */}
          {!isInstalled && (installPrompt || installSuccess) && (
            <div className="mt-4">
              {installSuccess ? (
                <div className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-success/10 border border-success/20 text-success text-sm font-600">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  App installed successfully!
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleInstall}
                  className="
                    w-full flex items-center justify-center gap-2
                    border border-primary/30 text-primary text-sm font-600
                    py-2.5 rounded-lg bg-primary/5
                    hover:bg-primary/10 active:scale-[0.99] transition-all duration-150
                  "
                >
                  <Download size={15} />
                  Install App
                </button>
              )}
            </div>
          )}

          {/* Always-visible install hint for browsers that don't fire beforeinstallprompt */}
          {!isInstalled && !installPrompt && !installSuccess && (
            <div className="mt-4 px-3 py-2.5 bg-muted rounded-lg flex items-center gap-2">
              <Download size={14} className="text-muted-foreground shrink-0" />
              <p className="text-xs text-muted-foreground">
                To install the app, use your browser&apos;s <span className="font-600">Add to Home Screen</span> option.
              </p>
            </div>
          )}

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Contact your administrator to get access.
          </p>
        </div>
      </div>
    </div>
  );
}